import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { DocumentImageList, GeneralLookup } from 'services/openapi';
import { CustomDeletePopup } from 'app/components/CustomDeletePopup';
import useSettingsApi from 'admin/hooks/useSettingsApi';
import { DocumentImageCardSkeletonLoader } from './DocumentImageCardSkeletonLoader';
import { AdminCustomPopUp } from 'app/components/AdminCustomPopUp';
import { CommonLoader } from 'app/components/CommonLoader';
import AdminCustomButton from 'app/components/AdminCustomButton';
import AdminDropDown from 'app/components/AdminDropDown';
import { formatDisplayOption, getDisplayOptionLookup } from './context';
import AdminCustomImageCropper from 'admin/components/AdminCustomImageCropper';
import SimpleReactValidator from 'simple-react-validator';
import { Errortext } from 'app/components/AdminTextField';
import { DisplayOption } from 'services/openapi';
import { formatFileName } from 'utils/common';

const DashboardContainer = styled.div`
    padding: 15px 0;
`;

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 20px;

    @media (min-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: 1024px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (min-width: 1280px) {
        grid-template-columns: repeat(4, 1fr);
    }
`;

const ImageCard = styled.div`
    border: 1px solid #dceaff;
    border-radius: 8px;
    background-color: #edf4fd99;
    overflow: hidden;
    transition: all 0.2s ease-in-out;
    display: flex;
    flex-direction: column;
    height: 100%;

    &:hover {
        box-shadow: 0 4px 12px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
        transform: translateY(-2px);
    }
`;

const ImagePreview = styled.div<{ imageUrl?: string }>`
    width: 100%;
    height: 180px;
    background-color: #f3f4f6;
    background-image: ${props =>
        props.imageUrl ? `url('${props.imageUrl.replace(/\\/g, '/')}')` : 'none'};
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
`;

const CardContent = styled.div`
    padding: 18px 16px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
`;

const CardTitle = styled.h3`
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
    letter-spacing: 0.05em;
    display: flex;
    align-items: center;
    line-height: 1.3;
    word-break: break-word;
`;

const CardSubtitle = styled.p`
    font-size: 11px;
    color: #1f2937;
    margin: 0 0 12px 0;
    letter-spacing: 0.05em;
    display: flex;
    align-items: center;
    line-height: 1.3;
    word-break: break-word;
`;

const CardActions = styled.div`
    display: flex;
    gap: 8px;
    margin-top: auto;
`;

const Button = styled.button<{ variant?: 'primary' | 'danger' }>`
    flex: 1;
    padding: 8px 12px;
    border: 1px solid ${props => (props.variant === 'danger' ? '#ef4444' : '#3b82f6')};
    background-color: ${props =>
        props.variant === 'danger' ? '#fee2e2' : '#eff6ff'};
    color: ${props => (props.variant === 'danger' ? '#dc2626' : '#1d4ed8')};
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
        background-color: ${props =>
        props.variant === 'danger' ? '#fecaca' : '#dbeafe'};
        border-color: ${props => (props.variant === 'danger' ? '#dc2626' : '#1d4ed8')};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const HiddenFileInput = styled.input`
    display: none;
`;

const NoDataContainer = styled.div`
    grid-column: 1 / -1;
    padding: 40px 20px;
    text-align: center;
    color: #6b7280;
    font-size: 16px;
`;

const PlaceholderIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
`;

const FlexWrapper = styled.div`
  padding: 10px 20px 15px 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-height: 800px;
  overflow-y: auto;

  .drop-down {
    padding: 6px 0 10px 0;
    .label {
      font-size: 12px !important;
      color: rgb(23, 23, 23) !important;
    }
    .k-input-value-text {
      font-size: 12px !important;
      color: rgb(23, 23, 23) !important;
    }
  }
`;

const ImageUploadArea = styled.div<{ isDragActive?: boolean }>`
  border: 2px dashed ${props => props.isDragActive ? '#3b82f6' : '#cbd5e0'};
  border-radius: 8px;
  padding: 30px 20px;
  text-align: center;
  background-color: ${props => props.isDragActive ? '#eff6ff' : '#f9fafb'};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 150px;

  &:hover {
    border-color: #3b82f6;
    background-color: #eff6ff;
  }
`;

const UploadIcon = styled.div`
  font-size: 32px;
  margin-bottom: 10px;
`;

const UploadText = styled.p`
  font-size: 14px;
  color: #1f2937;
  margin: 0;
  font-weight: 500;
`;

const UploadSubText = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 5px 0 0 0;
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 200px;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(220, 38, 38, 1);
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
  width: 100%;
  gap: 10px;
  align-items: baseline;
  margin-top: 10px;
  .button-style {
    button {
      max-width: unset !important;
      width: auto !important;
    }
  }
  .field-style {
    display: flex;
  }
`;
const FullImagePreviewModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
`;

const ModalContent = styled.div`
    position: relative;
    max-width: 90vw;
    max-height: 90vh;
    background-color: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    overflow: auto;
`;

const ModalImage = styled.img`
    width: 100%;
    height: auto;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 4px;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 15px;
    right: 15px;
    background-color: rgba(0, 0, 0, 0.6);
    color: white;
    border: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    transition: background-color 0.2s;

    &:hover {
        background-color: rgba(0, 0, 0, 0.9);
    }
`;

const ImageInfoPanel = styled.div`
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ImageInfo = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #6b7280;
    gap: 10px;

    .label {
        font-weight: 600;
        color: #1f2937;
    }
`;

const PreviewButton = styled.button`
   position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: rgb(255 255 255 / 72%);
    color: #000;
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    cursor: pointer;
    opacity: 1; 
    transition: opacity 0.2s;
    font-size: 13px;
    font-weight: 600;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;

    &:hover {
        background-color: rgba(59, 130, 246, 1);
          color: #fff;
    }
`;

// Helper function to convert DisplayOption to numeric value
const getDisplayOptionNumber = (displayOptionId: string | DisplayOption | undefined): number => {
    if (!displayOptionId) return 0;

    switch (displayOptionId) {
        case DisplayOption.TWO_PER_ROW:
            return 0;
        case DisplayOption.FULL_PAGE:
            return 1;
        default:
            return 0;
    }
};

interface ImageCardComponentProps {
    image: DocumentImageList;
    onUpload: (file: File, displayOption?: number, OnSuccess?: () => void) => void;
    onDelete: (imageId: string, OnSuccess?: () => void) => void;
    isLoading?: boolean;
}

const DocumentImageCardComponent: React.FC<ImageCardComponentProps> = ({
    image,
    onUpload,
    onDelete,
    isLoading = false,
}) => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [isCardLoading, setIsCardLoading] = useState(false);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [showFullPreview, setShowFullPreview] = useState(false);
    const [imageInfo, setImageInfo] = useState<{ width: number; height: number }>({
        width: 0,
        height: 0,
    });

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setIsCardLoading(true);
            const displayOptionNumber = image.displayOption !== undefined
                ? getDisplayOptionNumber(image.displayOption)
                : 0;
            onUpload(file, displayOptionNumber, () => {
                setIsCardLoading(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            });
        }
    };

    const handleDeleteClick = () => {
        setShowDeletePopup(true);
    };

    const handleDeleteConfirm = async () => {
        setIsCardLoading(true);
        if (image?.id) {
            onDelete(image.id, () => {
                setIsCardLoading(false);
                setShowDeletePopup(false);
            });
        }
    };

    const handleDeleteCancel = () => {
        setShowDeletePopup(false);
    };

    const handlePreviewClick = async () => {
        if (displayImageUrl) {
            const img = new Image();
            img.onload = async () => {
                try {

                    setImageInfo({
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                    });
                } catch (error) {
                    setImageInfo({
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                    });
                }
            };
            img.src = displayImageUrl;
        }
        setShowFullPreview(true);
    };

    const displayImageUrl = image.path
        ? image.path.replace(/\\/g, '/')
        : undefined;

    return (
        <>
            <ImageCard>
                <ImagePreview imageUrl={displayImageUrl}>
                    {!displayImageUrl && (
                        <PlaceholderIcon>
                            <svg width="80" height="80" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="15" y="15" width="90" height="90" rx="4" stroke="#CBD5E0" strokeWidth="2" fill="none" />
                                <path d="M25 85L45 60L60 75L95 40V85H25Z" fill="#E2E8F0" />
                                <path d="M25 85L45 60L60 75L95 40" stroke="#CBD5E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                <circle cx="75" cy="35" r="8" fill="#E2E8F0" />
                            </svg>
                        </PlaceholderIcon>
                    )}
                    {displayImageUrl && (
                        <PreviewButton
                            onClick={handlePreviewClick}
                            title="View full image with correct aspect ratio"
                        >
                            Preview
                        </PreviewButton>
                    )}
                </ImagePreview>
                <CardContent>
                    <CardTitle>{formatFileName(image?.fileName ?? "")}</CardTitle>
                    <CardSubtitle>{formatDisplayOption(image.displayOption)}</CardSubtitle>
                    <CardActions>
                        {displayImageUrl && (
                            <Button
                                variant="danger"
                                onClick={handleDeleteClick}
                                disabled={isCardLoading || isLoading}
                            >
                                Delete
                            </Button>
                        )}
                    </CardActions>
                </CardContent>
                <HiddenFileInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                />
            </ImageCard>

            {/* Full Preview Modal */}
            {showFullPreview && displayImageUrl && (
                <FullImagePreviewModal onClick={() => setShowFullPreview(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <CloseButton onClick={() => setShowFullPreview(false)}>
                            ✕
                        </CloseButton>
                        <ModalImage src={displayImageUrl} alt={image?.fileName ?? ""} />

                        <ImageInfoPanel>
                            <ImageInfo>
                                <span className="label">File Name:</span>
                                <span>{formatFileName(image?.fileName ?? "")}</span>
                            </ImageInfo>
                            <ImageInfo>
                                <span className="label">Dimensions:</span>
                                <span>{imageInfo.width} × {imageInfo.height} px</span>
                            </ImageInfo>
                            <ImageInfo>
                                <span className="label">Display Option:</span>
                                <span>{formatDisplayOption(image.displayOption)}</span>
                            </ImageInfo>
                        </ImageInfoPanel>
                    </ModalContent>
                </FullImagePreviewModal>
            )}

            {/* Delete Popup */}
            <CustomDeletePopup
                deleteIcon={true}
                headerUnderline={false}
                popupSubLabel="Are you sure you want to delete this image?"
                className="body-scroll-lock height-full delete-update"
                popupLabel="Delete Image"
                setVisible={handleDeleteCancel}
                visible={showDeletePopup}
                align="centre"
                btnLabel1="Cancel"
                btnLabel2="Delete"
                handlebtn2Click={handleDeleteConfirm}
                handlebtn1Click={handleDeleteCancel}
            />
        </>
    );
};

interface Props {
    isAddDocumentImageModalOpen: boolean;
    setIsAddDocumentImageModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const DocumentImageDashboard: React.FC<Props> = ({
    isAddDocumentImageModalOpen,
    setIsAddDocumentImageModalOpen
}) => {
    const {
        documentImageLoading,
        uploadImageLoading,
        commonDocumentImageList,
        fetchCommonDocumentImages,
        uploadDocumentImages,
        deleteDocumentImage,
    } = useSettingsApi();

    const [selectedDisplayOption, setSelectedDisplayOption] = useState<GeneralLookup>({ id: '', name: '' });
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState('');
    const [imagePreview, setImagePreview] = useState<string>('');
    const [isDragActive, setIsDragActive] = useState(false);
    const [showCropper, setShowCropper] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ file?: string }>({});
    const [forceUpdate, setForceUpdate] = useState(false);
    const displayOptionOptions = getDisplayOptionLookup();
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const simpleValidator = useRef(new SimpleReactValidator());

    useEffect(() => {
        fetchCommonDocumentImages();
    }, []);

    const handleUpload = (file: File, displayOption?: number, OnSuccess?: () => void) => {
        uploadDocumentImages([file], displayOption, () => {
            fetchCommonDocumentImages();
            OnSuccess?.();
        });
    };

    const handleDelete = (imageId: string, OnSuccess?: () => void) => {
        deleteDocumentImage(imageId, () => {
            fetchCommonDocumentImages();
            OnSuccess?.();
        });
    };

    const handleDisplayOptionChange = (data: GeneralLookup) => {
        setSelectedDisplayOption(data);
        simpleValidator.current?.showMessageFor('Image Display Option');
    };

    const handleCropComplete = (croppedBlob: Blob, croppedImageUrl: string) => {
        const croppedFile = new File(
            [croppedBlob],
            selectedFile?.name || 'cropped-image.png',
            { type: 'image/png' }
        );
        setSelectedFile(croppedFile);
        setImagePreview(croppedImageUrl);
        setShowCropper(false);

        // Clear file error when user selects/crops image
        if (validationErrors.file) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.file;
                return newErrors;
            });
        }
    };

    const handleCropCancel = () => {
        setShowCropper(false);
        setSelectedFile(null);
        setImagePreview('');
    };

    const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                alert('Invalid file format. Only PNG, JPG, JPEG, and WEBP files are allowed.');
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }
            processFile(file);
        }
    };

    const processFile = (file: File) => {
        setSelectedFile(file);

        // Generate preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result as string);
            setShowCropper(true);
        };
        reader.readAsDataURL(file);

        // Set file name if empty
        if (!fileName) {
            setFileName(file.name.replace(/\.[^/.]+$/, '')); // Remove extension
        }
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];

            // Validate file type
            if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                alert('Invalid file format. Only PNG, JPG, JPEG, and WEBP files are allowed.');
                return;
            }

            processFile(file);
        }
    };

    const handleRemoveImage = () => {
        setSelectedFile(null);
        setImagePreview('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const validateForm = (): boolean => {
        const errors: { file?: string } = {};

        if (!selectedFile) {
            errors.file = 'Image is required';
        }

        // Let AdminDropDown validate the display option
        const isDisplayOptionValid = !!selectedDisplayOption?.id;

        setValidationErrors(errors);
        return Object.keys(errors).length === 0 && isDisplayOptionValid;
    };

    const handleSaveImage = () => {
        if (!validateForm()) {
            simpleValidator.current?.showMessages();
            setForceUpdate(!forceUpdate);
            return;
        }

        if (selectedFile && selectedDisplayOption?.id) {
            const displayOptionNumber = getDisplayOptionNumber(selectedDisplayOption.id);
            console.log('displayOptionNumberdisplayOptionNumber', displayOptionNumber);
            handleUpload(selectedFile, displayOptionNumber, () => {
                // Reset form
                resetModal();

            });
        }
    };

    const resetModal = () => {
        setSelectedFile(null);
        setFileName('');
        setImagePreview('');
        setSelectedDisplayOption({ id: '', name: '' });
        setShowCropper(false);
        setValidationErrors({});
        setIsAddDocumentImageModalOpen(false);
        simpleValidator.current?.hideMessages();
    };

    if (documentImageLoading) {
        return (
            <DashboardContainer>
                <GridContainer>
                    <DocumentImageCardSkeletonLoader count={commonDocumentImageList?.length || 12} />
                </GridContainer>
            </DashboardContainer>
        );
    }

    return (
        <DashboardContainer>
            <GridContainer>
                {!commonDocumentImageList || commonDocumentImageList.length === 0 ? (
                    <NoDataContainer>
                        No document images available
                    </NoDataContainer>
                ) : (
                    commonDocumentImageList.map((image) => (
                        <DocumentImageCardComponent
                            key={image.id}
                            image={image}
                            onUpload={handleUpload}
                            onDelete={handleDelete}
                            isLoading={uploadImageLoading || documentImageLoading}
                        />
                    ))
                )}
            </GridContainer>

            <AdminCustomPopUp
                className="add-back-number-popup-style"
                visible={isAddDocumentImageModalOpen}
                setVisible={setIsAddDocumentImageModalOpen}
                closeToggle={resetModal}
                defaultPopupFooter={false}
                align="centre"
                popupLabel="Add Document Image"
                children={
                    <>
                        {uploadImageLoading ? (
                            <CommonLoader loaderType="login" />
                        ) : (
                            <FlexWrapper>
                                {/* Image Upload Area */}
                                {!imagePreview && (
                                    <div>
                                        <ImageUploadArea
                                            isDragActive={isDragActive}
                                            onDragEnter={handleDragEnter}
                                            onDragLeave={handleDragLeave}
                                            onDragOver={handleDragOver}
                                            onDrop={handleDrop}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <UploadIcon>
                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <rect x="2" y="2" width="20" height="20" rx="2" stroke="#3b82f6" strokeWidth="1.5" />
                                                    <circle cx="6.5" cy="6.5" r="1.5" fill="#3b82f6" />
                                                    <path d="M2 16L7 11L12 16L22 6V22C22 23.1 21.1 24 20 24H4C2.9 24 2 23.1 2 22V16Z" fill="#3b82f6" fillOpacity="0.3" />
                                                    <path d="M2 16L7 11L12 16L22 6" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </UploadIcon>
                                            <UploadText>Click to upload or drag and drop</UploadText>
                                            <UploadSubText>PNG, JPG, WEBP up to 10MB</UploadSubText>
                                        </ImageUploadArea>
                                        <HiddenFileInput
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/png,image/jpeg,image/jpg,image/webp"
                                            onChange={handleFileInputChange}
                                        />
                                        {validationErrors.file && (
                                            <Errortext>{validationErrors.file}</Errortext>
                                        )}
                                    </div>
                                )}

                                {/* Image Cropper */}
                                {showCropper && imagePreview && (
                                    <AdminCustomImageCropper
                                        imageUrl={imagePreview}
                                        onCropComplete={handleCropComplete}
                                        onCancel={handleCropCancel}
                                        defaultAspectRatio={1}
                                        minWidth={50}
                                        minHeight={50}
                                        showAspectRatioControls={true}
                                        cropButtonLabel="Crop & Continue"
                                        cancelButtonLabel="Cancel"
                                    />
                                )}

                                {/* Image Preview */}
                                {!showCropper && imagePreview && (
                                    <>
                                        <div>
                                            <ImagePreviewContainer>
                                                <PreviewImage src={imagePreview} alt="Preview" />
                                                <RemoveImageButton onClick={handleRemoveImage}>
                                                    ✕
                                                </RemoveImageButton>
                                            </ImagePreviewContainer>
                                        </div>

                                        {/* Display Option Dropdown - Only visible after cropping */}
                                        <AdminDropDown
                                            label="Image Display Option"
                                            placeholder="Select display option"
                                            onDataChange={handleDisplayOptionChange}
                                            defaultValue={selectedDisplayOption}
                                            data={displayOptionOptions}
                                            className="entries-dropdown-style"
                                            popupStyles="entries-dropdown-popup"
                                            validations="required"
                                            validator={simpleValidator.current}
                                        />

                                        {/* Action Buttons - Only visible after cropping */}
                                        <ButtonWrapper>
                                            <AdminCustomButton
                                                buttonType="cancel"
                                                onClick={resetModal}
                                            >
                                                Cancel
                                            </AdminCustomButton>
                                            <AdminCustomButton
                                                buttonType="primary"
                                                isActive={!!(selectedFile && selectedDisplayOption?.id)}
                                                className="button-style"
                                                onClick={handleSaveImage}
                                            >
                                                Save
                                            </AdminCustomButton>
                                        </ButtonWrapper>
                                    </>
                                )}
                            </FlexWrapper>
                        )}
                    </>
                }
            />
        </DashboardContainer>
    );
};

export default DocumentImageDashboard;